import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import {
  ColDef,
  GridOptions,
  GridApi,
  colorSchemeLight,
  iconSetMaterial,
  themeMaterial,
} from 'ag-grid-community';
import { ActionConfig } from './table-action-configs';
import { TableConfig, TableActionEvent } from './table.interfaces';
import { TableColumnService } from './services/table-column.service';
import { GridConfigService } from './services/grid-config.service';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent implements OnInit, OnDestroy {
  @ViewChild('agGrid') agGrid!: ElementRef;

  @Input() rowData: any[] = [];
  @Input() columnDefs: ColDef[] = [];
  @Input() columnPreset: string = 'DEFAULT';
  @Input() customColumnPresets: Record<string, ColDef[]> = {};
  @Input() gridOptions: GridOptions = {};
  @Input() config: TableConfig = {};
  @Input() enableActions: boolean = false;
  @Input() customActions: ActionConfig[] = [];
  @Input() loading: boolean = false;
  @Input() height: string = '';
  @Input() enableExport: boolean = false;
  @Input() exportFileName: string = 'table-export';
  @Input() enablePagination: boolean = true;
  @Input() paginationPageSize: number = 10;
  @Input() enableRowSelection: boolean = true;
  @Input() rowSelectionMode: 'single' | 'multiple' = 'multiple';
  @Input() enableSorting: boolean = true;
  @Input() enableFiltering: boolean = true;
  @Input() enableColumnResize: boolean = true;
  @Input() enableColumnReorder: boolean = true;
  @Input() showToolPanel: boolean = false;
  @Input() frameworkComponents: any = {};
  @Input() rowClassRules: any = {};

  @Output() actionClicked = new EventEmitter<TableActionEvent>();
  @Output() rowSelected = new EventEmitter<any[]>();
  @Output() cellClicked = new EventEmitter<any>();
  @Output() cellDoubleClicked = new EventEmitter<any>();
  @Output() resourceClicked = new EventEmitter<any>();
  @Output() gridReady = new EventEmitter<any>();
  @Output() dataChanged = new EventEmitter<any>();
  @Output() sortChanged = new EventEmitter<any>();
  @Output() filterChanged = new EventEmitter<any>();

  public gridApi!: GridApi;
  public customTheme = themeMaterial
    .withPart(iconSetMaterial)
    .withPart(colorSchemeLight)
    .withParams({
      backgroundColor: '#ffffff',
      foregroundColor: '#1A1A1A',
      headerBackgroundColor: '#f8f9fa',
      oddRowBackgroundColor: 'transparent',
      selectedRowBackgroundColor: 'rgba(0, 63, 45, 0.12)',
      fontFamily:
        '"Calibre-R", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '14px',
      spacing: '8px',
      headerHeight: '56px',
      rowHeight: '3rem',
    });

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressSizeToFit: false,
    minWidth: 150,
    flex: 1,
    maxWidth: 300,
  };

  public autoSizeStrategy: any = { type: 'fitGridWidth' };

  private readonly eventListeners: (() => void)[] = [];
  private resizeTimeout: any;

  constructor(
    private readonly columnService: TableColumnService,
    private readonly configService: GridConfigService,
  ) {}

  ngOnInit() {
    this.setupColumns();
    this.setupGridOptions();
  }

  ngOnDestroy() {
    for (const cleanup of this.eventListeners) {
      cleanup();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  private setupColumns() {
    let columns =
      this.columnDefs.length > 0
        ? this.columnDefs
        : this.columnService.getColumnsFromPreset(
            this.columnPreset,
            this.customColumnPresets,
          );

    if (this.enableActions || this.config.enableActions) {
      const actions =
        this.customActions.length > 0
          ? this.customActions
          : this.columnService.getActionsForPreset(this.columnPreset);

      columns = this.columnService.addActionColumn(
        columns,
        actions,
        (action: string, rowData: any) => {
          this.actionClicked.emit({
            action: action as any,
            id: rowData.id || rowData.employeeId,
            rowData: rowData,
          });
        },
      );
    }

    this.columnDefs = columns;
  }

  private setupGridOptions() {
    const config = {
      enablePagination: this.enablePagination,
      paginationPageSize: this.paginationPageSize,
      enableRowSelection: this.enableRowSelection,
      rowSelectionMode: this.rowSelectionMode,
      enableSorting: this.enableSorting,
      enableFiltering: this.enableFiltering,
      enableColumnResize: this.enableColumnResize,
      showToolPanel: this.showToolPanel,
    };

    const callbacks = {
      onResourceClick: this.handleResourceClick.bind(this),
      onGridReady: this.handleGridReady.bind(this),
      onSelectionChanged: this.handleSelectionChanged.bind(this),
      onCellClicked: this.handleCellClicked.bind(this),
      onCellDoubleClicked: this.handleCellDoubleClicked.bind(this),
      onSortChanged: this.handleSortChanged.bind(this),
      onFilterChanged: this.handleFilterChanged.bind(this),
      // onColumnMoved: this.handleColumnMoved.bind(this),
    };

    const isMyRequestPage = window.location.pathname.includes('my-request');
    const configuredGridOptions = this.configService.createGridOptions(
      config,
      callbacks,
    );

    // Merge gridOptions properly, preserving context from input
    this.gridOptions = {
      ...configuredGridOptions, // Base configuration
      ...this.gridOptions, // Input gridOptions (preserves context, domLayout, etc.)
      // Merge contexts explicitly to avoid overwriting
      context: {
        ...configuredGridOptions.context,
        ...this.gridOptions.context, // Preserve parent context (onResourceClick, componentParent, etc.)
      },
      rowClassRules: {
        ...this.rowClassRules,
        ...configuredGridOptions.rowClassRules,
        ...(isMyRequestPage && {
          'status-delivered': (params: any) =>
            params.data.status?.toLowerCase() === 'delivered',
        }),
      },
    };
  }

  private setDefaultDataIfNeeded() {
    if (this.rowData.length === 0) {
      this.rowData = [
        {
          id: 1,
          name: 'John Doe',
          email: 'john.doe@example.com',
          status: 'Active',
        },
        {
          id: 2,
          name: 'Jane Smith',
          email: 'jane.smith@example.com',
          status: 'Active',
        },
        {
          id: 3,
          name: 'Bob Johnson',
          email: 'bob.johnson@example.com',
          status: 'Inactive',
        },
        {
          id: 4,
          name: 'Alice Brown',
          email: 'alice.brown@example.com',
          status: 'Active',
        },
      ];
    }
  }

  handleGridReady(params: any) {
    this.gridApi = params.api;
    setTimeout(() => {
      // Use sizeColumnsToFit to fill the grid width
      this.gridApi.sizeColumnsToFit();
    }, 100);
    this.gridReady.emit(params);
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.gridApi) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(
        () => this.gridApi.sizeColumnsToFit(),
        200,
      );
    }
  }

  handleSelectionChanged(event: any) {
    const selectedRows = event.api.getSelectedRows();
    this.rowSelected.emit(selectedRows);
  }

  handleCellClicked(event: any) {
    this.cellClicked.emit(event);
  }

  handleCellDoubleClicked(event: any) {
    this.cellDoubleClicked.emit(event);
  }

  handleResourceClick(resourceData: any) {
    this.resourceClicked.emit(resourceData);
  }

  handleSortChanged(event: any) {
    this.sortChanged.emit(event);
  }

  handleFilterChanged(event: any) {
    this.filterChanged.emit(event);
  }

  public refreshData() {
    if (this.gridApi) {
      this.gridApi.refreshCells();
      setTimeout(() => {
        const resourceCol = this.gridApi.getColumn('resourceName');
        if (resourceCol) {
          this.gridApi.autoSizeColumns(['resourceName'], false);
        }
      }, 100);
    }
  }

  public exportToCsv(filename?: string) {
    if (this.gridApi && this.enableExport) {
      this.gridApi.exportDataAsCsv({
        fileName: filename || `${this.exportFileName}.csv`,
      });
    }
  }

  public clearSelection() {
    if (this.gridApi) {
      this.gridApi.deselectAll();
    }
  }

  public selectAll() {
    if (this.gridApi) {
      this.gridApi.selectAll();
    }
  }

  public getSelectedRows() {
    return this.gridApi ? this.gridApi.getSelectedRows() : [];
  }

  public updateRowData(data: any[]) {
    this.rowData = data;
    if (this.gridApi) {
      this.gridApi.applyTransaction({ update: data });
    }
  }
}
